import { useState, useEffect } from "react";
import { getEmployeeReviews, getTenantById } from "../../../../../shared/services/apis";
import IframeViewer from "../../../../../shared/ui/urlOpener/IFrameViewer";
import saveAs from "file-saver";
import { RiDownloadLine } from "react-icons/ri";

export interface CompanyInsights {
  citations: string[];
  search_results: SearchResult[];
  content: CompanyContent;
}

export interface SearchResult {
  url: string;
  date: string | null;
  title: string;
  last_updated: string | null;
}

export interface CompanyContent {
  company: string;
  website: string;
  reviews: Review[];
  summary: Summary;
  sources: string[];
}

export interface Review {
  date_of_review: string;
  star_rating: string; // e.g. "5/5"
  review_text: string;
  summary: string;
  source: string;
  themes: string[];
}

export interface Summary {
  total_reviews: number;
  average_rating: string; // e.g. "3.3/5"
  positive_themes: string[];
  negative_themes: string[];
  overall_sentiment: string;
}

interface EmployeeReviewsProps {
  tenantId: string;
  tenantServiceId: string;
  tenantServiceOptionId: string;
}

interface Tenant {
  id: string;
  name: string;
  logoUrl?: string;
}


export const EmployeeReviews: React.FC<EmployeeReviewsProps> = ({
  tenantId,
  tenantServiceId,
  tenantServiceOptionId,
}) => {
  const [data, setData] = useState<CompanyInsights | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTheme, setSelectedTheme] = useState<string | null>(null);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingWord, setDownloadingWord] = useState(false);
  const [tenant, setTenant] = useState<Tenant | null>(null);

  useEffect(() => {
    if (!tenantId) return;

    const fetchTenant = async () => {
      try {
        const data = await getTenantById(tenantId);
        setTenant(data);
      } catch (error) {
        console.error("Error fetching tenant:", error);
      }
    };

    fetchTenant();
  }, [tenantId]);

  // Extract unique themes from all reviews
  const allThemes = Array.from(
    new Set(
      data?.content?.reviews?.flatMap((review) => review.themes || []) || []
    )
  );

  useEffect(() => {
    if (!tenantId || !tenantServiceId || !tenantServiceOptionId) return;

    const fetchData = async () => {
      try {
        const response = await getEmployeeReviews(
          tenantId,
          tenantServiceId,
          tenantServiceOptionId
        );
        setData(response);
      } catch (err) {
        console.error("Failed to fetch employee reviews:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [tenantId, tenantServiceId, tenantServiceOptionId]);

  const handleSourceClick = (e: React.MouseEvent, url: string) => {
    e.preventDefault();
    setIframeUrl(url);
  };

  const downloadPDF = async () => {
    setDownloadingPdf(true);
    try {
      const { jsPDF } = await import("jspdf");
      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const marginX = 20;
      const marginY = 25;
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const contentWidth = pageWidth - marginX * 2;

      // Header
      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.text("Employee Reviews", marginX, marginY);

      // Line under header
      doc.setDrawColor(190);
      doc.setLineWidth(0.5);
      doc.line(marginX, marginY + 3, pageWidth - marginX, marginY + 3);

      // Metadata
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(80);
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, marginX, marginY + 12);
      doc.text(`Tenant: ${tenant?.name || "N/A"}`, marginX, marginY + 19);

      // Summary
      const sentiment = calculateSentiment();
      const totalReviews = data?.content?.reviews.length || 0;

      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(40);
      doc.text("Review Summary", marginX, marginY + 30);

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(`Total Reviews: ${totalReviews}`, marginX, marginY + 38);
      doc.text(`Positive Reviews: ${sentiment.positive.toFixed(1)}%`, marginX, marginY + 45);
      doc.text(`Negative Reviews: ${sentiment.negative.toFixed(1)}%`, marginX, marginY + 52);

      let currentY = marginY + 62;

      // Reviews
      const filteredReviews = selectedTheme
        ? data?.content?.reviews.filter(r => r.themes.includes(selectedTheme)) || []
        : data?.content?.reviews || [];

      filteredReviews.forEach((review) => {
        if (currentY > pageHeight - 50) {
          doc.addPage();
          currentY = marginY;
        }

        // Date
        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(100);
        const dateText = new Date(review.date_of_review).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
        doc.text(dateText, marginX, currentY);
        currentY += 6;

        // Rating
        const rating = parseFloat(review.star_rating);
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(rating < 3 ? 200 : 80);
        doc.text(`Rating: ${review.star_rating}`, marginX, currentY);
        currentY += 6;

        // Themes
        if (review.themes.length > 0) {
          doc.setFontSize(8);
          doc.setTextColor(150);
          doc.text(`Themes: ${review.themes.join(", ")}`, marginX, currentY);
          currentY += 8;
        }

        // Review text
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(60);
        const lines = doc.splitTextToSize(review.review_text, contentWidth);
        doc.text(lines, marginX, currentY);
        currentY += lines.length * 6;

        // Summary
        if (review.summary) {
          doc.setFontSize(9);
          doc.setTextColor(100);
          const summaryLines = doc.splitTextToSize(review.summary, contentWidth);
          doc.text(summaryLines, marginX, currentY);
          currentY += summaryLines.length * 6;
        }

        currentY += 12;
      });

      // Watermark and footer on all pages
      if (tenant?.logoUrl) {
        try {
          const img = await new Promise<HTMLImageElement>((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = "Anonymous";
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = tenant.logoUrl as string;
          });

          const watermarkWidth = 90;
          const watermarkHeight = (img.height / img.width) * watermarkWidth;
          const xPos = (pageWidth - watermarkWidth) / 2;
          const yPos = (pageHeight - watermarkHeight) / 2;

          const totalPages = doc.getNumberOfPages();
          for (let i = 1; i <= totalPages; i++) {
            doc.setPage(i);
            
            // Add watermark
            doc.saveGraphicsState();
            doc.setGState(new (doc.GState as any)({ opacity: 0.07 }));
            doc.addImage(img, "PNG", xPos, yPos, watermarkWidth, watermarkHeight);
            doc.restoreGraphicsState();
            
            // Add footer
            doc.setFontSize(8);
            doc.setTextColor(130);
            doc.text(`Page ${i} of ${totalPages} | Confidential`, pageWidth / 2, pageHeight - 10, { align: "center" });
          }
        } catch (error) {
          console.warn("Failed to load watermark:", error);
        }
      }

      doc.save(`employee-reviews-${tenantId}-${new Date().getTime()}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      setDownloadingPdf(false);
    }
  };

  const downloadWord = async () => {
    setDownloadingWord(true);
    try {
      const {
        Document,
        Paragraph,
        TextRun,
        HeadingLevel,
        AlignmentType,
        ImageRun,
        Packer,
        convertInchesToTwip,
        PageOrientation,
        Footer,
        Table,
        TableRow,
        TableCell,
        WidthType,
      } = await import("docx");

      let logoImageData: { data: Uint8Array; mimeType: "jpg" | "png" | "gif" | "bmp" } | null = null;
      if (tenant?.logoUrl) {
        try {
          const response = await fetch(tenant?.logoUrl);
          const blob = await response.blob();
          
          const getMimeType = (blobType: string): "jpg" | "png" | "gif" | "bmp" => {
            if (blobType.includes("png")) return "png";
            if (blobType.includes("jpeg") || blobType.includes("jpg")) return "jpg";
            if (blobType.includes("gif")) return "gif";
            if (blobType.includes("bmp")) return "bmp";
            return "png";
          };

          const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const result = reader.result as string;
              const base64Data = result.split(",")[1];
              resolve(base64Data);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });

          const binaryString = window.atob(base64);
          const len = binaryString.length;
          const bytes = new Uint8Array(len);
          for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }

          logoImageData = { data: bytes, mimeType: getMimeType(blob.type) };
        } catch (error) {
          console.warn("Failed to load logo:", error);
        }
      }

      const filteredReviews = selectedTheme
        ? data?.content?.reviews.filter(r => r.themes.includes(selectedTheme)) || []
        : data?.content?.reviews || [];

      const doc = new Document({
        creator: "Your Application",
        title: "Employee Reviews",
        description: "Employee reviews document",
        sections: [
          {
            properties: {
              page: {
                margin: {
                  top: convertInchesToTwip(1),
                  right: convertInchesToTwip(0.75),
                  bottom: convertInchesToTwip(1),
                  left: convertInchesToTwip(0.75),
                },
                size: {
                  orientation: PageOrientation.PORTRAIT,
                },
              },
            },
            children: [
              new Paragraph({
                text: "Employee Reviews",
                heading: HeadingLevel.HEADING_1,
                alignment: AlignmentType.CENTER,
              }),
              new Paragraph({
                text: `Generated on: ${new Date().toLocaleDateString()}`,
                alignment: AlignmentType.CENTER,
              }),
              new Paragraph({
                text: `Tenant: ${tenant?.name || "N/A"}`,
                alignment: AlignmentType.CENTER,
              }),
              
              // Summary
              new Paragraph({
                text: "Review Summary",
                heading: HeadingLevel.HEADING_2,
                spacing: { before: 240, after: 120 },
              }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: `• Total Reviews: ${data?.content?.reviews.length || 0}`,
                    size: 10,
                    color: "444444",
                  }),
                ],
                spacing: { after: 100 },
              }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: `• Positive Reviews: ${calculateSentiment().positive.toFixed(1)}%`,
                    size: 10,
                    color: "444444",
                  }),
                ],
                spacing: { after: 100 },
              }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: `• Negative Reviews: ${calculateSentiment().negative.toFixed(1)}%`,
                    size: 10,
                    color: "444444",
                  }),
                ],
                spacing: { after: 200 },
              }),
              
              // Reviews Table
              new Table({
                rows: filteredReviews.map(review => {
                  return new TableRow({
                    children: [
                      new TableCell({
                        children: [
                          new Paragraph({
                            children: [
                              new TextRun({
                                text: new Date(review.date_of_review).toLocaleDateString("en-US", {
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                }),
                                bold: true,
                                size: 10,
                                color: "666666",
                              }),
                            ],
                            spacing: { after: 60 },
                          }),
                          new Paragraph({
                            children: [
                              new TextRun({
                                text: `Rating: ${review.star_rating}`,
                                size: 9,
                                color: parseFloat(review.star_rating) < 3 ? "CC0000" : "666666",
                              }),
                            ],
                          }),
                          new Paragraph({
                            children: [
                              new TextRun({
                                text: `Themes: ${review.themes.join(", ")}`,
                                size: 8,
                                color: "999999",
                              }),
                            ],
                            spacing: { after: 80 },
                          }),
                        ],
                        width: { size: 20, type: WidthType.PERCENTAGE },
                      
                      }),
                      new TableCell({
                        children: [
                          new Paragraph({
                            children: [
                              new TextRun({
                                text: review.review_text,
                                size: 10,
                                color: "333333",
                              }),
                            ],
                            spacing: { after: 100 },
                          }),
                          new Paragraph({
                            children: [
                              new TextRun({
                                text: review.summary,
                                size: 9,
                                color: "666666",
                              }),
                            ],
                          }),
                        ],
                        width: { size: 55, type: WidthType.PERCENTAGE },
                 
                      }),
                      new TableCell({
                        children: [
                          new Paragraph({
                            children: [
                              new TextRun({
                                text: new URL(review.source).hostname,
                                size: 9,
                                color: "0066CC",
                           
                              }),
                            ],
                            alignment: AlignmentType.CENTER,
                            spacing: { after: 150 },
                          }),
                        ],
                        width: { size: 25, type: WidthType.PERCENTAGE },
                       
                      }),
                    ],
                  });
                }),
                width: {
                  size: 100,
                  type: WidthType.PERCENTAGE,
                },
                layout: "fixed",
                columnWidths: [1500, 6000, 1500],
              }),
            ],
            footers: {
              default: new Footer({
                children: [
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: `Confidential - Generated by ${tenant?.name}`,
                        size: 10,
                        color: "888888",
                      }),
                    ],
                    alignment: AlignmentType.CENTER,
                  }),
                  ...(logoImageData
                    ? [
                        new Paragraph({
                          children: [
                            new ImageRun({
                              data: logoImageData.data,
                              type: logoImageData.mimeType,
                              transformation: { width: 60, height: 60 },
                            }),
                          ],
                          alignment: AlignmentType.RIGHT,
                          spacing: { before: 100 },
                        }),
                      ]
                    : []),
                  new Paragraph({
                    text: "",
                    spacing: { after: 100 },
                  }),
                ],
              }),
            },
          },
        ],
      });

      const blob = await Packer.toBlob(doc);
      const fileName = `employee-reviews-${tenantId}-${new Date().getTime()}.docx`;
      saveAs(blob, fileName);
    } catch (error) {
      console.error("Error generating Word document:", error);
    } finally {
      setDownloadingWord(false);
    }
  };


  if (loading) {
    return (
      <div className="bg-[#FDFDFD] rounded-xl max-w-4xl m-auto mt-0 2xl:mt-20 p-6 shadow-sm border border-gray-100 space-y-6">
        {/* Header skeleton */}
        <div className="space-y-3">
          <div className="h-4 w-24 bg-gray-200 animate-pulse rounded" />
          <div className="flex gap-2">
            <div className="h-6 w-20 bg-gray-200 animate-pulse rounded-full" />
            <div className="h-6 w-20 bg-gray-200 animate-pulse rounded-full" />
            <div className="h-6 w-20 bg-gray-200 animate-pulse rounded-full" />
          </div>
        </div>

        {/* Sentiment section skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <div className="h-4 w-32 bg-gray-200 animate-pulse rounded" />
            <div className="h-4 w-48 bg-gray-200 animate-pulse rounded" />
          </div>
          <div className="space-y-2">
            <div className="h-4 w-32 bg-gray-200 animate-pulse rounded" />
            <div className="h-4 w-48 bg-gray-200 animate-pulse rounded" />
          </div>
        </div>

        {/* Review skeletons */}
        {[...Array(3)].map((_, i) => (
          <div key={i} className="flex space-x-6 border-t border-gray-100 pt-4">
            <div className="min-w-[150px]">
              <div className="h-4 w-24 bg-gray-200 animate-pulse rounded" />
            </div>
            <div className="flex-1 space-y-2">
              <div className="h-4 w-24 bg-gray-200 animate-pulse rounded" />
              <div className="h-4 w-full bg-gray-200 animate-pulse rounded" />
              <div className="h-4 w-3/4 bg-gray-200 animate-pulse rounded" />
              <div className="h-6 w-24 bg-gray-200 animate-pulse rounded-xl" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!data) {
  return (
    <div className="flex items-center  justify-center gap-3 text-sm text-gray-700 bg-gray-50 border border-gray-200 px-4 py-3 rounded-lg mt-4 shadow-sm w-3xl m-auto">
      {/* Icon inside subtle circle */}
      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-200">
        <svg
          className="w-4 h-4 text-gray-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4m0 4h.01m-6.938 4h13.856c1.054 0 
               1.623-1.14 1.054-2.012L13.054 4.988c-.526-.827-1.582-.827-2.108 
               0L3.028 17.988c-.57.872.0001 2.012 1.054 2.012z"
          />
        </svg>
      </div>

      {/* Text + Retry button */}
      <div className="flex items-center gap-3">
        <span>Failed to load data. Please try again.</span>
      </div>
    </div>
  );
}


  // Calculate sentiment percentages based on star ratings
  const calculateSentiment = () => {
    if (!data.content?.reviews || data.content.reviews.length === 0) {
      return { positive: 0, negative: 0 };
    }

    const positiveCount = data.content.reviews.filter((review) => {
      const rating = parseFloat(review.star_rating);
      return rating >= 3;
    }).length;

    const negativeCount = data.content.reviews.filter((review) => {
      const rating = parseFloat(review.star_rating);
      return rating < 3;
    }).length;

    const total = data.content.reviews.length;
    const positive = (positiveCount / total) * 100;
    const negative = (negativeCount / total) * 100;

    return { positive, negative };
  };

  const sentiment = calculateSentiment();

  const filteredReviews = selectedTheme
    ? data.content.reviews.filter((review) =>
        review.themes.includes(selectedTheme)
      )
    : data.content.reviews;

  return (
    <div className="bg-[#FDFDFD] rounded-xl max-w-4xl m-auto mt-0 2xl:mt-20 ] shadow-sm border border-gray-100 mb-30">
        <div className="flex gap-2 my-2 mr-6  justify-end">
               <button
                 onClick={downloadPDF}
                 disabled={downloadingPdf}
                 className="flex items-center gap-2 hover:bg-gray-200 font-light px-4 py-2 rounded-full border border-gray-300 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
               >
                 {downloadingPdf ? (
                   <>
                     <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                       <circle
                         className="opacity-25"
                         cx="12"
                         cy="12"
                         r="10"
                         stroke="currentColor"
                         strokeWidth="4"
                         fill="none"
                       />
                       <path
                         className="opacity-75"
                         fill="currentColor"
                         d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                       />
                     </svg>
                     Generating...
                   </>
                 ) : (
                   <>
                     <RiDownloadLine className="w-4 h-4" /> PDF
                   </>
                 )}
               </button>
     
               <button
                 onClick={downloadWord}
                 disabled={downloadingWord}
                 className="flex items-center gap-2 hover:bg-gray-200 font-light px-4 py-2 rounded-full border border-gray-300 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
               >
                 {downloadingWord ? (
                   <>
                     <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                       <circle
                         className="opacity-25"
                         cx="12"
                         cy="12"
                         r="10"
                         stroke="currentColor"
                         strokeWidth="4"
                         fill="none"
                       />
                       <path
                         className="opacity-75"
                         fill="currentColor"
                         d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                       />
                     </svg>
                     Generating...
                   </>
                 ) : (
                   <>
                     <RiDownloadLine className="w-4 h-4" />
                     Word
                   </>
                 )}
               </button>
             </div>
      <div className="p-4 bg-[#F9FBFD] rounded-t-2xl">
        <div className="flex flex-wrap gap-2 px-6 mb-4">
          <button
            onClick={() => setSelectedTheme(null)}
            className={`border border-gray-300 px-3 py-1 rounded-full text-sm cursor-pointer ${
              selectedTheme === null
                ? "bg-gray-100 text-blue-700"
                : "text-gray-700 bg-white hover:bg-gray-50"
            }`}
          >
            All Reviews
          </button>
          {allThemes.map((theme, idx) => (
            <button
              key={idx}
              onClick={() =>
                setSelectedTheme((prev) => (prev === theme ? null : theme))
              }
              className={`border border-gray-300 px-3 py-1 rounded-full text-sm cursor-pointer ${
                selectedTheme === theme
                  ? "bg-gray-100 text-blue-700"
                  : "text-gray-700 bg-white hover:bg-gray-50"
              }`}
            >
              {theme}
            </button>
          ))}
        </div>

        {/* Sentiment Summary */}
        <div className="grid px-8 py-6 grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-700 mb-6">
          <div className="gap-2 items-center ">
            <span className="font-medium text-md flex mb-3">
              🟩{" "}
              {Number.isInteger(sentiment.positive)
                ? sentiment.positive
                : sentiment.positive.toFixed(1)}
              % Positive
            </span>
            <p className="text-gray-600">
              Most employees appreciated the work culture and management
              support.
            </p>
          </div>
          <div className="gap-2 items-center">
            <span className="font-medium text-md flex mb-3">
              🟥 {" "}
              {Number.isInteger(sentiment.negative)
                ? sentiment.negative
                : sentiment.negative.toFixed(1)}
              % Negative
            </span>
            <p className="text-gray-600">
              Some feedback mentioned challenges with documentation and
              workload.
            </p>
          </div>
        </div>
      </div>

      {/* Review List */}
      <div className="space-y-6 p-6">
        {filteredReviews.map((review, index) => (
          <div
            key={index}
            className="pt-4 flex space-x-6 pb-4 last:border-b-0 border-b border-gray-100"
          >
            <p className="text-sm text-gray-500 mb-1 min-w-[150px]">
              {new Date(review.date_of_review).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </p>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span
                  className={`text-md ${
                    parseFloat(review.star_rating) < 3
                      ? "text-red-500 font-light"
                      : "text-gray-600"
                  }`}
                >
                  Rating {review.star_rating}
                </span>
              </div>

              <p className="text-sm font-medium text-gray-800 mb-2 leading-relaxed">
                {review.review_text}
              </p>

              <p className="text-gray-600/95 mb-2">{review.summary}</p>

              <div className="flex items-center justify-between">
                <p
                  onClick={(e) => handleSourceClick(e, review.source)}
                  className="text-xs bg-white text-gray-500 px-4 py-1 border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50 transition"
                >
                  {/* {new URL(review.source).hostname} */}
                </p>
              </div>
            </div>
          </div>
        ))}

        {/* Citations Section */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <h3 className="text-sm font-medium text-gray-900 mb-3">Sources</h3>
          <div className="flex gap-2 flex-wrap">
            {(data.citations || []).map((url, idx) => (
              <button
                key={idx}
                onClick={(e) => handleSourceClick(e, url)}
                className="border border-gray-300 px-3 py-1 rounded-full text-sm text-gray-700 hover:bg-gray-50 transition cursor-pointer"
              >
                {new URL(url).hostname}
              </button>
            ))}
          </div>
        </div>
      </div>

      {iframeUrl && (
        <IframeViewer url={iframeUrl} onClose={() => setIframeUrl(null)} />
      )}
    </div>
  );
};
